import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { getTelehealthReportData } from '@/api/print';
import { MOCK_REPORT_DATA } from '@/api/print/mock-data';
import type { TelehealthReportData } from '@/api/print/types';

import { ReportHeader } from './report-header';
import { ReportFooter } from './report-footer';
import { ReportImagesGrid } from './report-images-grid';
import { ReportInsightsPage } from './report-insights-page';
import { ReportSummaryPage } from './report-summary-page';

declare global {
  interface Window {
    pdfReady?: boolean;
    pdfError?: string;
  }
}

export function TelehealthReportPrintPage() {
  const { gradeId } = useParams<{ gradeId: string }>();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') ?? '';
  const enableInsights =
    searchParams.get('enable_insights') === 'true' ||
    searchParams.get('enable_insights') === 'True';
  const useMock = searchParams.get('mock') === 'true';

  const [data, setData] = useState<TelehealthReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const totalImagesRef = useRef(0);
  const loadedImagesRef = useRef(0);
  const dataLoadedRef = useRef(false);

  const signalReady = useCallback(() => {
    requestAnimationFrame(() => {
      void document.fonts.ready.then(() => {
        window.pdfReady = true;
      });
    });
  }, []);

  const signalError = useCallback((message: string) => {
    window.pdfError = message;
  }, []);

  const checkAllReady = useCallback(() => {
    if (dataLoadedRef.current && loadedImagesRef.current >= totalImagesRef.current) {
      signalReady();
    }
  }, [signalReady]);

  const handleImageLoad = useCallback(() => {
    loadedImagesRef.current += 1;
    checkAllReady();
  }, [checkAllReady]);

  useEffect(() => {
    const fetchData = async () => {
      if (useMock) {
        const reportData = MOCK_REPORT_DATA;
        const mockImageCount = reportData.images?.length ?? 0;
        setData(reportData);
        totalImagesRef.current = mockImageCount;
        dataLoadedRef.current = true;
        setLoading(false);
        if (mockImageCount === 0) {
          checkAllReady();
        }
        return;
      }

      if (!gradeId || !token) {
        const msg = 'Missing grade ID or authentication token';
        setError(msg);
        setLoading(false);
        signalError(msg);
        return;
      }

      try {
        const reportData = await getTelehealthReportData(gradeId, token, enableInsights);
        const imageCount = reportData.images?.length ?? 0;
        setData(reportData);
        totalImagesRef.current = imageCount;
        dataLoadedRef.current = true;
        setLoading(false);

        if (imageCount === 0) {
          checkAllReady();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load report data';
        setError(msg);
        setLoading(false);
        signalError(msg);
      }
    };

    void fetchData();
  }, [gradeId, token, enableInsights, useMock, checkAllReady, signalReady, signalError]);

  if (loading) {
    return (
      <div className="print-page flex-full-center">
        <p className="text-sm text-gray-500">Loading report...</p>
      </div>
    );
  }

  if (error ?? !data) {
    return (
      <div className="print-page flex-full-center">
        <p className="text-sm text-red-600">Error: {error ?? 'No data available'}</p>
      </div>
    );
  }

  return (
    <div className="bg-white text-black">
      {/* Page 1: Header + Images */}
      <div className="print-page print-page-break">
        <ReportHeader data={data} />
        <ReportImagesGrid images={data.images ?? []} onImageLoad={handleImageLoad} />
        <ReportFooter />
      </div>

      {/* Page 2: Insights (conditional) */}
      {data.insights?.should_include && (
        <div className="print-page print-page-break">
          <ReportHeader data={data} />
          <ReportInsightsPage insights={data.insights} />
          <ReportFooter />
        </div>
      )}

      {/* Page 3: Summary */}
      <div className="print-page">
        <ReportHeader data={data} />
        <ReportSummaryPage grade={data.grade} />
        <ReportFooter />
      </div>
    </div>
  );
}
